/**
 * views/projects/useProjectStats.js - the Projects tab's derived numbers: the
 * filter popovers' value lists, top customers / approval managers, the stats,
 * the Won totals (and, on GLOBAL KSA, their currency) and the chip counts.
 * Moved verbatim out of ProjectsView.jsx.
 */
import { useMemo } from 'react';
import { leftoverNum } from '../../utils/projectMoney';
import { fxCode, fmtMoney as ksaFmtMoney } from '../../components/ksa/ksaMoney';
import { isActiveProject } from '../../data/projectStatus';
import { splitNames } from './tableBits';

export function useProjectStats({ COL_ACCESSORS, DATE_COLS, NUMERIC_COLS, sourceProjects, MULTI_COLS, scope, filtered }) {
  // Build unique-value lists per filterable column for the popover
  const columnUniqueValues = useMemo(() => {
    const cols = {};
    for (const [colId, accessor] of Object.entries(COL_ACCESSORS)) {
      // Date + numeric columns use a range filter, not a value-set — skip.
      if (DATE_COLS.has(colId) || NUMERIC_COLS.has(colId)) continue;
      const counts = new Map();
      sourceProjects.forEach((p) => {
        const v = accessor(p);
        if (v == null || v === '') return;
        for (const k of MULTI_COLS.has(colId) ? splitNames(v) : [String(v)]) counts.set(k, (counts.get(k) || 0) + 1);
      });
      cols[colId] = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }));
    }
    return cols;
  }, [sourceProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  const topApprovalManagers = useMemo(() => {
    const counts = new Map();
    sourceProjects.forEach((p) => {
      const am = p.approvalManager || p.approval_manager;
      if (!am) return;
      counts.set(am, (counts.get(am) || 0) + 1);
    });
    const list = [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([c]) => c);
    // Manual swap requested by leadership: Sergio should appear before Anthony.
    const fuzzy = (s, needle) => (s || '').toLowerCase().includes(needle);
    const sergioIdx = list.findIndex((n) => fuzzy(n, 'sergio'));
    const anthonyIdx = list.findIndex((n) => fuzzy(n, 'anthony'));
    if (sergioIdx > -1 && anthonyIdx > -1 && sergioIdx > anthonyIdx) {
      [list[anthonyIdx], list[sergioIdx]] = [list[sergioIdx], list[anthonyIdx]];
    }
    return list;
  }, [sourceProjects]);

  const topCustomers = useMemo(() => {
    const counts = new Map();
    sourceProjects.forEach((p) => {
      if (!p.customer) return;
      counts.set(p.customer, (counts.get(p.customer) || 0) + 1);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([c]) => c);
  }, [sourceProjects]);

  const stats = useMemo(() => {
    const won = sourceProjects.filter((p) => p.status === 'Won');
    const open = sourceProjects.filter((p) => p.status === 'Open');
    const lost = sourceProjects.filter((p) => p.status === 'Lost');
    const wonRevenue = won.reduce((s, p) => s + (Number(p.quotedPrice) || 0), 0);
    // Margin only includes rows where labor cost is populated; otherwise old rows with
    // no recorded cost would appear as 100%-margin and bias the average upward
    // (audit: 90% of 2022 Won and 81% of 2023 Won rows have NULL quotedLaborCost).
    const wonWithCost = won.filter((p) => Number(p.quotedLaborCost) > 0);
    const marginRevenue = wonWithCost.reduce((s, p) => s + (Number(p.quotedPrice) || 0), 0);
    const marginCogs = wonWithCost.reduce((s, p) => s + ((Number(p.quotedLaborCost) || 0) + (Number(p.quotedExpenses) || 0)), 0);
    const wonMargin = marginRevenue ? (marginRevenue - marginCogs) / marginRevenue : null;
    const openPipeline = open.reduce((s, p) => {
      const v = Number(p.quotedPrice) || 0;
      return s + (v < 500_000_000 ? v : 0);
    }, 0);
    const closedCount = won.length + lost.length;
    const winRate = closedCount ? won.length / closedCount : null;
    return { wonCount: won.length, openCount: open.length, lostCount: lost.length, wonRevenue, wonMargin, openPipeline, winRate };
  }, [sourceProjects]);

  // KSA: the totals carry the rows' currency; rows in different currencies cannot be added up as one
  const totalsCode = useMemo(() => {
    if (!scope) return null;
    const codes = new Set(filtered.filter((r) => r.status === 'Won').map((r) => fxCode(r.currency) || 'SAR'));
    return codes.size === 1 ? [...codes][0] : 'mixed currencies';
  }, [scope, filtered]);
  const totalsMoney = scope ? (v) => (Number(v) > 0 ? ksaFmtMoney(v, totalsCode) : '-') : undefined;
  const wonTotals = useMemo(() => {
    const won = filtered.filter((r) => r.status === 'Won');
    const sumPrice = won.reduce((acc, r) => acc + (Number(r.quotedPrice) || 0), 0);
    const sumLabor = won.reduce((acc, r) => acc + (Number(r.quotedLaborCost) || 0), 0);
    const sumExp = won.reduce((acc, r) => acc + (Number(r.quotedExpenses) || 0), 0);
    const sumCogs = sumLabor + sumExp;
    const profit = sumPrice - sumCogs;
    const sumLeftOver = won.reduce((s, r) => s + leftoverNum(r), 0);
    const sumInvoiced = won.reduce((s, r) => s + (Number(r.invoicedAlready ?? r.invoiced_already) || 0), 0);
    // Margin denominator should only include rows that have labor cost recorded;
    // otherwise the average is inflated by uncosted historical rows.
    const wonWithCost = won.filter((r) => Number(r.quotedLaborCost) > 0);
    const marginSell = wonWithCost.reduce((s, r) => s + (Number(r.quotedPrice) || 0), 0);
    const marginCogs = wonWithCost.reduce((s, r) => s + ((Number(r.quotedLaborCost) || 0) + (Number(r.quotedExpenses) || 0)), 0);
    const margin = marginSell ? (marginSell - marginCogs) / marginSell : null;
    return { count: won.length, sumPrice, sumCogs, profit, sumLeftOver, sumInvoiced, margin };
  }, [filtered]);

  const statusCounts = useMemo(() => {
    // isActiveProject is the sidebar Projects badge's rule too, so the badge
    // always equals the Active chip this tab opens on.
    const active = sourceProjects.filter(isActiveProject).length;
    return { All: sourceProjects.length, Active: active, Closed: sourceProjects.length - active };
  }, [sourceProjects]);
  return { wonTotals, totalsCode, stats, statusCounts, topCustomers, topApprovalManagers, totalsMoney, columnUniqueValues };
}
